import { Box, Link, Typography } from '@mui/material'
import { useLayoutEffect } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { COLORS } from '../../../styles/variables'
import { getLegalDocumentPath } from './legalDocumentPaths'
import {
  legalBulletListSx,
  legalMetaSx,
  legalPageSx,
  legalParagraphSx,
  legalRelatedLinksListSx,
  legalRelatedLinksSx,
  legalRelatedLinksTitleSx,
  legalSectionSx,
  legalSectionTitleSx,
  legalSubsectionTitleSx,
  legalTitleSx,
} from './style'
import type { LegalDocumentContent, LegalSection } from './types'
import { LegalRichText } from './LegalRichText'

type LegalDocumentPageProps = {
  content: LegalDocumentContent
}

function LegalSectionBlock({ section }: { section: LegalSection }) {
  return (
    <Box component="section" sx={legalSectionSx}>
      <Typography variant="h2" sx={legalSectionTitleSx}>
        {section.title}
      </Typography>
      {section.paragraphs?.map((paragraph) => (
        <LegalRichText key={paragraph} text={paragraph} />
      ))}
      {section.bullets && section.bullets.length > 0 && (
        <Box component="ul" sx={legalBulletListSx}>
          {section.bullets.map((bullet) => (
            <Typography component="li" key={bullet}>
              {bullet}
            </Typography>
          ))}
        </Box>
      )}
      {section.subsections?.map((subsection) => (
        <Box key={subsection.id}>
          <Typography component="h3" sx={legalSubsectionTitleSx}>
            {subsection.title}
          </Typography>
          {subsection.paragraphs?.map((paragraph) => (
            <LegalRichText key={paragraph} text={paragraph} />
          ))}
          {subsection.bullets && subsection.bullets.length > 0 && (
            <Box component="ul" sx={legalBulletListSx}>
              {subsection.bullets.map((bullet) => (
                <Typography component="li" key={bullet}>
                  {bullet}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  )
}

export function LegalDocumentPage({ content }: LegalDocumentPageProps) {
  useLayoutEffect(() => {
    document.title = content.documentTitle
  }, [content.documentTitle])

  return (
    <Box sx={legalPageSx}>
      <Typography variant="h1" sx={legalTitleSx}>
        {content.h1}
      </Typography>
      <Typography variant="body2" sx={legalMetaSx}>
        Редакция {content.version} от {content.effectiveDate}
      </Typography>

      {content.intro.map((paragraph) => (
        <Typography key={paragraph} sx={legalParagraphSx}>
          {paragraph}
        </Typography>
      ))}

      {content.sections.map((section) => (
        <LegalSectionBlock key={section.id} section={section} />
      ))}

      {content.relatedLinks.length > 0 && (
        <Box sx={legalRelatedLinksSx}>
          <Typography sx={legalRelatedLinksTitleSx}>См. также</Typography>
          <Box sx={legalRelatedLinksListSx}>
            {content.relatedLinks.map((link) => (
              <Link
                key={link.slug}
                component={RouterLink}
                to={getLegalDocumentPath(link.slug)}
                underline="hover"
                sx={{ color: COLORS.NICE_BLUE, fontSize: 14 }}
              >
                {link.label}
              </Link>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}
